import React, { useEffect, useState } from 'react';
import './App.css';

const STORAGE_KEY = 'percalc_dates';
// coefficient applied to (1/level) when distributing mass to children
const LEVEL_COEFF = 100.0;

function formatDateInput(d) {
  if (!d) return '';
  const dt = new Date(d);
  const tzOff = dt.getTimezoneOffset() * 60000;
  return new Date(dt - tzOff).toISOString().slice(0, 10);
}

function daysBetween(a, b) {
  const A = new Date(a);
  const B = new Date(b);
  const diff = Math.ceil((B - A) / (1000 * 60 * 60 * 24));
  return diff;
}

function addMonths(date, months) {
  const d = new Date(date);
  const m = d.getMonth();
  d.setMonth(m + months);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function App() {
  const [dates, setDates] = useState(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      const normalized = parsed.map((it, i) => ({ id: it.id || Date.now() + i, date: it.date || it }));
      return normalized.sort((a, b) => new Date(b.date) - new Date(a.date));
    } catch (e) {
      console.error('Failed to parse dates from storage', e);
      return [];
    }
  });
  const [editingId, setEditingId] = useState(null);
  const [newDate, setNewDate] = useState('');

  const [months, setMonths] = useState(1);
  const [futureDays, setFutureDays] = useState([]);
  const [distribution, setDistribution] = useState([]);
  const [futureCounts, setFutureCounts] = useState({});
  const [windowCounts, setWindowCounts] = useState({});
  const [levelCoeff, setLevelCoeff] = useState(LEVEL_COEFF);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dates));
  }, [dates]);

  function addDate() {
    if (!newDate) return;
    const id = Date.now();
    const entry = { id, date: newDate };
    setDates((s) => [...s, entry].sort((a, b) => new Date(b.date) - new Date(a.date)));
    setNewDate('');
  }

  function startEdit(id) {
    setEditingId(id);
  }

  function saveEdit(id, dateVal) {
    setDates((s) => s.map((it) => (it.id === id ? { ...it, date: dateVal } : it)).sort((a, b) => new Date(b.date) - new Date(a.date)));
    setEditingId(null);
  }

  function removeDate(id) {
    setDates((s) => s.filter((it) => it.id !== id));
  }

  async function copyDataToClipboard() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY) || '[]';
      await navigator.clipboard.writeText(raw);
      alert('Dates copied to clipboard');
    } catch (e) {
      console.error('Copy failed', e);
      alert('Failed to copy to clipboard');
    }
  }

  async function pasteDataFromClipboard() {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) {
        alert('Clipboard is empty');
        return;
      }
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        alert('Clipboard does not contain valid JSON');
        return;
      }
      if (!Array.isArray(parsed)) {
        alert('Expected a JSON array of dates');
        return;
      }
      if (!window.confirm('This will overwrite saved dates. Continue?')) return;
      const normalized = parsed.map((it, i) => ({ id: it.id || Date.now() + i, date: it.date || it }));
      setDates(normalized.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (e) {
      console.error('Paste failed', e);
      alert('Failed to read from clipboard');
    }
  }

  function calculate() {
    setCalculating(true);
    setTimeout(() => {
      // determine start: use last recorded date if available, otherwise today
        const lastRecorded = dates.length > 0 ? [...dates].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-1)[0] : null;
        const start = lastRecorded ? new Date(lastRecorded.date) : new Date();
        const end = addMonths(start, parseInt(months || 0));
      const days = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        days.push(new Date(d));
      }
      setFutureDays(days.map((d) => new Date(d)));

      // distribution from differences between consecutive added dates
      if (dates.length < 2) {
        setDistribution([]);
        setFutureCounts({});
        setWindowCounts({});
        setCalculating(false);
        return;
      }
      const sorted = [...dates].sort((a, b) => new Date(a.date) - new Date(b.date));
      const diffs = [];
      for (let i = 1; i < sorted.length; i++) {
        diffs.push(daysBetween(sorted[i - 1].date, sorted[i].date));
      }
      const counts = {};
      diffs.forEach((d) => {
        counts[d] = (counts[d] || 0) + 1;
      });
      const total = diffs.length;
      const dist = Object.keys(counts)
        .map((k) => ({ days: parseInt(k), count: counts[k], percent: (counts[k] / total) * 100 }))
        .sort((a, b) => a.days - b.days);
      setDistribution(dist);

      // compute counters for future days based on distribution
      const countsMap = {};
      if (dist.length > 0 && dates.length > 0) {
        // map future days by ISO date
        const futureSet = new Set(days.map((d) => formatDateInput(d)));
        // get last chronological date from added dates
        const last = [...dates].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-1)[0];
        if (last) {
          // BFS with level and mass propagation.
          // Each queue node carries a remaining mass; start with mass=1 at root.
          // When expanding a node at level `level`, the node distributes mass * (1/level)
          // among its children proportionally to `row.count` weights so the children's
          // masses sum to mass*(1/level).
          const queue = [{ date: new Date(last.date), level: 1, mass: 1 }];
          // const totalWeight = dist.reduce((s, r) => s + (r.count || 0), 0) || 0;
          while (queue.length) {
            const { date: cur, level, mass } = queue.shift();
            if (!dist || dist.length === 0) continue;
            const denom = dist.reduce((s, r) => s + (r.count || 0), 0);
            if (!denom) continue;
            for (const row of dist) {
              const next = addDays(cur, row.days);
              if (next > end) continue;
              // child mass is parent's mass multiplied by a level-decay factor.
              // Use exponent = 1 / levelCoeff so increasing levelCoeff slows convergence.
              const safeCoeff = Math.max(0.0001, levelCoeff);
              const levelFactor = 1 / Math.pow(Math.max(1, level), 1 / safeCoeff);
              const childMass = mass * levelFactor * ((row.count || 0) / denom);
              // enqueue next for further expansion with increased level and its mass
              queue.push({ date: new Date(next), level: level + 1, mass: childMass });
              const key = formatDateInput(next);
              if (futureSet.has(key)) {
                countsMap[key] = (countsMap[key] || 0) + childMass;
              }
            }
          }
        }
      }
      setFutureCounts(countsMap);

      // compute 7-day window counts based on futureCounts (for each count, increment that day + next 6 days)
      const windowMap = {};
      if (Object.keys(countsMap).length > 0) {
        // reuse futureSet calculated above
        const futureSet = new Set(days.map((d) => formatDateInput(d)));
        for (const key of Object.keys(countsMap)) {
          const cnt = countsMap[key] || 0;
          const baseDate = new Date(key);
          for (let off = 0; off < 7; off++) {
            const target = addDays(baseDate, off);
            const k = formatDateInput(target);
            if (!futureSet.has(k)) continue;
            windowMap[k] = (windowMap[k] || 0) + cnt;
          }
        }
        // Ensure the first 7 days from the start have counter value 1
        for (let i = 0; i < 7; i++) {
          const firstKey = formatDateInput(addDays(start, i));
          if (futureSet.has(firstKey)) {
            windowMap[firstKey] = 1;
          }
        }
      }
      setWindowCounts(windowMap);
      setCalculating(false);
    }, 20);
  }

  return (
    <div className="App container py-4">
      <h1 className="mb-4">Period calculator</h1>
      <div className="row">
        <section className="col-md-4">
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">Settings</h5>
              <div className="row g-2 align-items-center mb-3">
                <div className="col">
                  <input className="form-control" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
                </div>
                <div className="col-auto d-flex gap-2">
                  <button className="btn btn-primary" onClick={addDate}>Add</button>
                  <button className="btn btn-outline-secondary" onClick={copyDataToClipboard}>Copy</button>
                  <button className="btn btn-outline-secondary" onClick={pasteDataFromClipboard}>Paste</button>
                </div>
              </div>

              <div style={{ maxHeight: 200, overflow: 'auto' }}>
                {dates.length === 0 && <div className="text-muted">No dates added.</div>}
                <ul className="list-group">
                  {dates.map((it) => (
                    <li key={it.id} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <div className="small text-muted">{formatDateInput(it.date)}</div>
                      </div>
                      <div className="btn-group">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => startEdit(it.id)}>Edit</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => removeDate(it.id)}>Delete</button>
                      </div>
                      {editingId === it.id && (
                        <div className="w-100 mt-2">
                          <EditableDate item={it} onCancel={() => setEditingId(null)} onSave={(dateVal) => saveEdit(it.id, dateVal)} />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
                {calculating && (
                  <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{background: 'rgba(0,0,0,0.4)', zIndex: 1050}}>
                    <div className="text-center text-white p-3" style={{background: 'rgba(0,0,0,0.6)', borderRadius: 8}}>
                      <div className="spinner-border text-light" role="status" style={{width: '3rem', height: '3rem'}}></div>
                      <div className="mt-2">Calculating...</div>
                    </div>
                  </div>
                )}
              <h5 className="card-title">Calendar</h5>
              <div className="d-flex align-items-center mb-3">
                <label className="me-2 mb-0">Months:</label>
                <input className="form-control" style={{ width: 120 }} type="number" min={0} value={months} onChange={(e) => setMonths(e.target.value)} />
                <div className="ms-3 d-flex align-items-center">
                  <label className="me-2 mb-0">Level coeff:</label>
                  <input className="form-control" style={{ width: 120 }} type="number" step="0.1" min={0.01} value={levelCoeff} onChange={(e) => setLevelCoeff(Number(e.target.value) || 0)} />
                </div>
                <button className="btn btn-primary ms-3" onClick={calculate}>Calculate</button>
              </div>

              <div className="card mt-3">
                <div className="card-body">
                    <h5 className="card-title">Calendar View</h5>
                    <div>
                        {(() => {
                        if (futureDays.length === 0) return <div className="text-muted">No days. Click Calculate.</div>;
                        // group futureDays by month
                        const monthsMap = {};
                        futureDays.forEach((d) => {
                            const key = `${d.getFullYear()}-${d.getMonth()}`;
                            monthsMap[key] = monthsMap[key] || [];
                            monthsMap[key].push(new Date(d));
                        });
                        const monthKeys = Object.keys(monthsMap).sort((a, b) => {
                            const [ay, am] = a.split('-').map(Number);
                            const [by, bm] = b.split('-').map(Number);
                            return new Date(ay, am) - new Date(by, bm);
                        });

                        const maxWindow = Math.max(...Object.values(windowCounts || {}).map(Number), 0);

                        return monthKeys.map((mk) => {
                            const parts = mk.split('-').map(Number);
                            const year = parts[0];
                            const month = parts[1];
                            const days = monthsMap[mk].sort((a, b) => a - b);
                            // weekday index of the first displayed day for this month with Monday = 0
                            // If this is the first month shown and it's a fragment (doesn't include day 1),
                            // align using the weekday of the first displayed future day. Otherwise use the month's 1st.
                            const isFirstShownMonth = mk === monthKeys[0];
                            const firstShownDay = days.length > 0 ? days[0] : null;
                            let firstWeekday;
                            if (isFirstShownMonth && firstShownDay && firstShownDay.getDate() !== 1) {
                            firstWeekday = (firstShownDay.getDay() + 6) % 7;
                            } else {
                            firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
                            }
                            // build slots with indentation so weekdays align
                            const slots = [];
                            for (let i = 0; i < firstWeekday; i++) slots.push(null);
                            days.forEach((d) => slots.push(d));
                            while (slots.length % 7 !== 0) slots.push(null);
                            const rows = [];
                            for (let i = 0; i < slots.length; i += 7) rows.push(slots.slice(i, i + 7));

                            return (
                            <div key={mk} className="mb-3">
                                <div className="fw-bold mb-2">{new Date(year, month).toLocaleString(undefined, { month: 'long', year: 'numeric' })}</div>
                                {rows.map((row, ri) => (
                                <div key={ri} className="d-flex mb-1">
                                    {row.map((cell, di) => {
                                    if (!cell) {
                                        return <div key={di} className="me-1" style={{ width: 40, height: 36 }} />;
                                    }
                                    const iso = formatDateInput(cell);
                                    const cnt = windowCounts[iso] || 0;
                                    const pct = maxWindow > 0 ? (cnt / maxWindow) * 100 : 0;
                                    const alpha = Math.max(0, Math.min(1, pct / 100));
                                    const bg = alpha === 0 ? '#ffffff' : `rgba(255,0,0,${alpha.toFixed(2)})`;
                                    return (
                                        <div key={di} className="d-flex align-items-center justify-content-center me-1" style={{ width: 40, height: 36, background: bg, color: '#000000ff' }}>{cell.getDate()}</div>
                                    );
                                    })}
                                </div>
                                ))}
                            </div>
                            );
                        });
                        })()}
                    </div>
                </div>
            </div>

              <div className="row">
                <div className="col-md-6">
                  <h6>Future Days</h6>
                  <div style={{ maxHeight: 300, overflow: 'auto' }}>
                    {futureDays.length === 0 && <div className="text-muted">No days. Click Calculate.</div>}
                    {(() => {
                      const maxCount = Math.max(...Object.values(futureCounts || {}).map(Number), 0);
                      return (
                        <ul className="list-group">
                          {futureDays.map((d, i) => {
                                const iso = formatDateInput(d);
                                const cnt = futureCounts[iso] || 0;
                                const pct = maxCount > 0 ? ((cnt / maxCount) * 100) : 0;
                            return (
                              <li key={i} className="list-group-item">
                                <div className="d-flex align-items-center">
                                  <div className="flex-grow-1">
                                    <div>{d.toDateString()}</div>
                                  </div>
                                  <div className="mx-2 d-flex align-items-center">
                                        {cnt > 0 && <span className="badge bg-danger rounded-pill">{Number(cnt).toFixed(2)}</span>}
                                        {cnt > 0 && <span className="badge bg-danger ms-1">{Number(pct).toFixed(2)}%</span>}
                                  </div>
                                  <div style={{ width: 160 }}>
                                    <div className="progress" style={{ height: 8 }}>
                                      <div className="progress-bar bg-danger" role="progressbar" style={{ width: `${pct}%` }} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} />
                                    </div>
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      );
                    })()}
                  </div>
                </div>
                <div className="col-md-6">
                  <h6>Windowed Counts (7-day spread)</h6>
                  <div style={{ maxHeight: 300, overflow: 'auto' }}>
                    {futureDays.length === 0 && <div className="text-muted">No days. Click Calculate.</div>}
                    {(() => {
                      const maxWindow = Math.max(...Object.values(windowCounts || {}).map(Number), 0);
                      return (
                        <ul className="list-group">
                          {futureDays.map((d, i) => {
                                const iso = formatDateInput(d);
                                const cnt = windowCounts[iso] || 0;
                                const pct = maxWindow > 0 ? ((cnt / maxWindow) * 100) : 0;
                            return (
                              <li key={i} className="list-group-item">
                                <div className="d-flex align-items-center">
                                  <div className="flex-grow-1">
                                    <div>{d.toDateString()}</div>
                                  </div>
                                  <div className="mx-2 d-flex align-items-center">
                                        {cnt > 0 && <span className="badge bg-danger rounded-pill">{Number(cnt).toFixed(2)}</span>}
                                        {cnt > 0 && <span className="badge bg-danger ms-1">{Number(pct).toFixed(2)}%</span>}
                                  </div>
                                  <div style={{ width: 160 }}>
                                    <div className="progress" style={{ height: 8 }}>
                                      <div className="progress-bar bg-danger" role="progressbar" style={{ width: `${pct}%` }} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} />
                                    </div>
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      );
                    })()}
                  </div>
                </div>

                <div className="col-md-6">
                  <h6>Distribution (gaps between added dates)</h6>
                  {distribution.length === 0 ? (
                    <div className="text-muted">No distribution — add at least two dates and Calculate.</div>
                  ) : (
                    <table className="table table-sm table-striped">
                      <thead>
                        <tr>
                          <th>Days</th>
                          <th className="text-end">Count</th>
                          <th className="text-end">Percent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {distribution.map((row) => (
                          <tr key={row.days}>
                            <td>{row.days}</td>
                            <td className="text-end">{row.count}</td>
                            <td className="text-end">{row.percent.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function EditableDate({ item, onSave, onCancel }) {
  const [dateVal, setDateVal] = useState(formatDateInput(item.date));
  return (
    <div className="d-flex gap-2">
      <input className="form-control form-control-sm" type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} />
      <div className="btn-group">
        <button className="btn btn-sm btn-primary" onClick={() => onSave(dateVal)}>Save</button>
        <button className="btn btn-sm btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default App;
